import type { AccountPermission } from "@app/store/types";
import type { AllCategory } from "@app/services/category/types";

export { RBAC_ACTIONS, RBAC_LABELS, MODULE_PERMISSION_PREFIX } from "./permissionsConstants";

const DEFAULT_ACTIONS = ["read", "create", "edit", "delete", "approve", "reject"] as const;
type ActionKey = (typeof DEFAULT_ACTIONS)[number];

export type PermissionsMap = Record<string, Record<ActionKey, boolean>>;

const normalizeBoolean = (value: unknown): boolean =>
  value === true || value === 1 || value === "1";

export const normalizePrefix = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, "")
    .toLowerCase();

const cleanPath = (path: unknown): string => {
  if (!path || typeof path !== "string") {
    return "";
  }
  let pathname = path;
  try {
    if (/^https?:\/\//i.test(path)) {
      pathname = new URL(path).pathname;
    } else {
      [pathname] = path.split("?");
    }
  } catch {
    [pathname] = path.split("?");
  }
  const trimmed = pathname.trim();
  const withSlash = trimmed && !trimmed.startsWith("/") ? `/${trimmed}` : trimmed;
  if (withSlash.length > 1 && withSlash.endsWith("/")) {
    return withSlash.slice(0, -1);
  }
  return withSlash;
};

export const sanitizeRoutePath = cleanPath;

export const normalizePermissions = (permissionList: AccountPermission[]): PermissionsMap => {
  if (!Array.isArray(permissionList)) {
    return {};
  }
  return permissionList.reduce<PermissionsMap>((acc, item) => {
    const prefix = normalizePrefix(item?.functionality?.prefix);
    if (!prefix) {
      return acc;
    }
    acc[prefix] = DEFAULT_ACTIONS.reduce<Record<ActionKey, boolean>>(
      (actionsAcc, actionName) => {
        actionsAcc[actionName] = normalizeBoolean(item?.[actionName]);
        return actionsAcc;
      },
      {} as Record<ActionKey, boolean>
    );
    return acc;
  }, {});
};

export const buildMapFromCategories = (
  categories: AllCategory[],
  idProfile: number
): PermissionsMap => {
  const map: PermissionsMap = {};
  for (const cat of categories ?? []) {
    for (const mod of cat.modules ?? []) {
      for (const func of mod.functionalities ?? []) {
        const prefix = normalizePrefix(func.prefix);
        if (!prefix) {
          continue;
        }
        const hasRead = (func.permissions ?? []).some(
          (p) => p.id_profile === idProfile && p.read === 1
        );
        if (!map[prefix]) {
          map[prefix] = {
            read: hasRead,
            create: hasRead,
            edit: hasRead,
            delete: hasRead,
            approve: hasRead,
            reject: hasRead
          };
        }
      }
    }
  }
  return map;
};

export const can = ({
  map,
  action,
  prefix
}: {
  map: PermissionsMap;
  action: string;
  prefix: string;
}): boolean => {
  if (!map || !action || !prefix) {
    return false;
  }
  const normalizedAction = String(action).toLowerCase() as ActionKey;
  const normalizedPrefix = normalizePrefix(prefix);
  const prefixPermissions = map[normalizedPrefix];
  if (!prefixPermissions?.read) {
    return false;
  }
  return Boolean(prefixPermissions[normalizedAction]);
};

export const matchPrefixByPathname = ({
  pathname,
  categories
}: {
  pathname: string;
  categories: AllCategory[];
}): string => {
  const normalizedPath = cleanPath(pathname);
  if (!normalizedPath || !Array.isArray(categories)) {
    return "";
  }
  for (const cat of categories) {
    for (const mod of cat.modules ?? []) {
      for (const func of mod.functionalities ?? []) {
        const funcPath = cleanPath(func.url);
        if (funcPath && funcPath === normalizedPath) {
          return normalizePrefix(func.prefix);
        }
      }
    }
  }
  return "";
};
